/**
 * BulkNovelEvalPanel.jsx
 *
 * Admin panel for batch-processing up to 20 novels simultaneously.
 * Features:
 *  - Searchable project selection grid (up to 20)
 *  - Dimension checkboxes (plot, arcs, structure, pacing, continuity)
 *  - Mode toggle: Analysis Markup vs Automated Rewrite
 *  - LLM mix-and-match selectors (Analysis + Rewrite passes)
 *  - Prompt caching indicator per LLM
 *  - Plot Extraction context toggle
 *  - Pre-run confirmation popup with cost estimate
 *  - Live progress bar (per-novel status)
 *  - Results viewer with per-novel findings + accept/reject
 *  - Batch history table
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi } from '@/lib/api';
import ExtractionReviewPanel from '@/components/panels/ExtractionReviewPanel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  DollarSign,
  Flame,
  Info,
  Layers,
  Loader2,
  Play,
  RefreshCw,
  Search,
  Settings2,
  Sparkles,
  Square,
  Wind,
  XCircle,
  Zap,
  FileDown,
  Upload,
  FileArchive,
  Users,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { bulkNovelEvalApi } from '@/lib/api';
import { PanelHeader } from './_shared';

const POLL_MS = 4000;

// ── Dimension catalogue ────────────────────────────────────────────────────
const DIMENSIONS = [
  {
    key: 'plot_elements',
    label: 'Plot Elements',
    desc: 'Exposition, inciting incident, rising action, climax, resolution',
    icon: '📐',
  },
  {
    key: 'character_arcs',
    label: 'Character Arcs',
    desc: 'Arc completeness, motivation consistency, flat vs dynamic',
    icon: '🔄',
  },
  {
    key: 'story_structure',
    label: 'Story Structure',
    desc: 'Act balance, subplot weaving, scene-sequel ratio, thematic coherence',
    icon: '🏗',
  },
  {
    key: 'pacing',
    label: 'Pacing & Flow',
    desc: 'Slow/rushed chapters, pacing cliffs, rhythm variety',
    icon: '⏱',
  },
  {
    key: 'continuity',
    label: 'Continuity',
    desc: 'Timeline errors, character description inconsistencies, fact contradictions',
    icon: '🔗',
  },
];

// Models that support Anthropic prompt caching
const CACHE_SUPPORTED_PROVIDERS = ['anthropic'];

// ── Score colour helpers ───────────────────────────────────────────────────
function scoreColor(score) {
  if (score == null) return 'text-muted-foreground';
  if (score >= 80) return 'text-emerald-600';
  if (score >= 50) return 'text-amber-600';
  return 'text-rose-600';
}

function scoreBg(score) {
  if (score == null) return 'bg-muted/30 border-border';
  if (score >= 80) return 'bg-emerald-50 border-emerald-200';
  if (score >= 50) return 'bg-amber-50 border-amber-200';
  return 'bg-rose-50 border-rose-200';
}

// ── Caching indicator dot ─────────────────────────────────────────────────
function CacheDot({ provider, cachingConfig }) {
  const isSupported = CACHE_SUPPORTED_PROVIDERS.includes(provider);
  const isEnabled = cachingConfig?.bulk ?? true;
  if (!isSupported) {
    return (
      <span
        className="inline-block h-2 w-2 rounded-full bg-zinc-300 ml-1"
        title="Prompt caching not supported for this provider"
      />
    );
  }
  return (
    <span
      className={cn(
        'inline-block h-2 w-2 rounded-full ml-1',
        isEnabled ? 'bg-emerald-500' : 'bg-amber-400',
      )}
      title={isEnabled ? 'Prompt caching ON (bulk task)' : 'Prompt caching OFF for bulk task'}
    />
  );
}

// ── Model select with cache indicator ─────────────────────────────────────
function ModelSelect({ value, onChange, models, label, cachingConfig, testId }) {
  const selectedModel = models.find((m) => m.id === value);
  const provider = selectedModel?.provider || '';
  return (
    <div className="space-y-1">
      <Label className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
        {label}
        <CacheDot provider={provider} cachingConfig={cachingConfig} />
      </Label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="h-9 w-full rounded-md border border-border bg-background px-2.5 text-[12.5px] focus:outline-none focus:ring-1 focus:ring-ring"
      >
        {models.map((m) => (
          <option key={m.id} value={m.id}>
            {m.label} ({m.cost_tier || m.provider})
          </option>
        ))}
      </select>
    </div>
  );
}

// ── Project card in selection grid ────────────────────────────────────────
function ProjectCard({ project, selected, onToggle, disabled }) {
  const score = project.plot_readiness?.overall_score;
  const wordCount = project.word_count || 0;
  return (
    <div
      onClick={() => !disabled && onToggle(project.id)}
      data-testid={`bulk-eval-project-${project.id}`}
      className={cn(
        'relative rounded-md border cursor-pointer transition-all p-3 space-y-1',
        selected
          ? 'border-primary bg-primary/8 ring-1 ring-primary/30'
          : 'border-border bg-card hover:border-primary/40',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      <div className="flex items-start gap-2">
        <div
          className={cn(
            'mt-0.5 h-4 w-4 rounded shrink-0 border-2 flex items-center justify-center',
            selected ? 'border-primary bg-primary' : 'border-muted-foreground/40',
          )}
        >
          {selected && <CheckCircle2 className="h-3 w-3 text-primary-foreground" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[12.5px] font-semibold truncate text-foreground leading-tight">
            {project.title || '(Untitled)'}
          </p>
          <p className="text-[10.5px] text-muted-foreground mt-0.5">
            {project.genre || 'No genre'} ·{' '}
            {wordCount > 0 ? `${(wordCount / 1000).toFixed(1)}k words` : `${project.chapter_count || 0} ch.`}
          </p>
        </div>
        {score != null && (
          <span className={cn('text-[11px] font-bold tabular-nums shrink-0', scoreColor(score))}>
            {score}%
          </span>
        )}
      </div>
    </div>
  );
}

// ── Dimension result accordion ────────────────────────────────────────────
function DimResultCard({ dimKey, findings }) {
  const [open, setOpen] = useState(false);
  const label = DIMENSIONS.find((d) => d.key === dimKey)?.label || dimKey;
  const score = findings?.overall_score;
  const error = findings?.error;

  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/15 transition-colors"
      >
        <span className={cn('text-[12px] font-semibold flex-1', error ? 'text-rose-600' : '')}>
          {label}
        </span>
        {error ? (
          <Badge className="border border-rose-200 bg-rose-50 text-rose-700 text-[10px]">Error</Badge>
        ) : score != null ? (
          <span className={cn('text-[12px] font-bold tabular-nums', scoreColor(score))}>
            {score}%
          </span>
        ) : null}
        {open ? (
          <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        )}
      </button>
      {open && (
        <div className="border-t border-border p-3">
          {error ? (
            <p className="text-[11.5px] text-rose-600">{error}</p>
          ) : (
            <pre className="whitespace-pre-wrap text-[11px] text-muted-foreground font-mono max-h-64 overflow-auto">
              {JSON.stringify(findings, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// ── Per-novel result card ─────────────────────────────────────────────────
function NovelResultCard({ result, onAccept, onReject, onRequeue, accepting, rejecting, requeuing }) {
  const [expanded, setExpanded] = useState(false);
  const isPending   = result.status === 'pending';
  const isRequeue   = result.status === 'needs_reeval';
  const isError     = result.status === 'error';

  return (
    <div
      className={cn(
        'rounded-md border p-3 space-y-2',
        result.status === 'accepted'    ? 'border-emerald-200 bg-emerald-50/50' :
        result.status === 'rejected'    ? 'border-zinc-200 bg-zinc-50/50' :
        result.status === 'needs_reeval'? 'border-amber-200 bg-amber-50/50' :
        isError                         ? 'border-rose-200 bg-rose-50/50' :
        'border-border bg-card',
      )}
      data-testid={`bulk-eval-result-${result.result_id}`}
    >
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold truncate">{result.project_title}</p>
          <p className="text-[10.5px] text-muted-foreground">
            Overall score:{' '}
            <span className={cn('font-bold', scoreColor(result.overall_score))}>
              {result.overall_score ?? '—'}%
            </span>
            {result.rewrite_chapters?.length > 0 && (
              <span className="ml-2">{result.rewrite_chapters.length} chapters rewritten</span>
            )}
            {isRequeue && (
              <span className="ml-2 text-amber-600 font-medium">Re-evaluation queued…</span>
            )}
            {isError && (
              <span className="ml-2 text-rose-600 font-medium">Error — click to re-evaluate</span>
            )}
          </p>
        </div>
        {result.status === 'accepted' && (
          <Badge className="border border-emerald-300 bg-emerald-100 text-emerald-800 text-[10px]">Accepted</Badge>
        )}
        {result.status === 'rejected' && (
          <Badge className="border border-zinc-300 bg-zinc-100 text-zinc-700 text-[10px]">Rejected</Badge>
        )}
        {isRequeue && (
          <Badge className="border border-amber-300 bg-amber-100 text-amber-700 text-[10px]">
            <Loader2 className="mr-1 h-2.5 w-2.5 animate-spin inline" />Re-evaluating
          </Badge>
        )}
        {/* Re-evaluate button — always visible so admin can re-queue any result */}
        <button
          type="button"
          title="Mark as Needs Re-evaluation and re-queue"
          disabled={requeuing || isRequeue}
          data-testid={`bulk-eval-requeue-${result.result_id}`}
          onClick={() => onRequeue(result.result_id, result.project_id)}
          className="flex items-center gap-1 rounded border border-border bg-secondary/40 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground transition-colors hover:border-amber-400 hover:bg-amber-50 hover:text-amber-700 disabled:opacity-40"
        >
          {requeuing ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <RefreshCw className="h-2.5 w-2.5" />}
          Re-eval
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-muted-foreground hover:text-foreground"
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {expanded && (
        <div className="space-y-2 pt-1">
          {Object.entries(result.dim_findings || {}).map(([key, findings]) => (
            <DimResultCard key={key} dimKey={key} findings={findings} />
          ))}

          {isPending && (
            <div className="flex items-center gap-2 pt-1">
              {result.rewrite_chapters?.length > 0 ? (
                <>
                  <Button
                    size="sm"
                    className="h-7 text-[11.5px] gap-1.5"
                    disabled={accepting}
                    data-testid={`bulk-eval-accept-${result.result_id}`}
                    onClick={() => onAccept(result.result_id)}
                  >
                    {accepting ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                    Accept & Apply Rewrite
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11.5px] gap-1.5"
                    disabled={rejecting}
                    data-testid={`bulk-eval-reject-${result.result_id}`}
                    onClick={() => onReject(result.result_id)}
                  >
                    <XCircle className="h-3 w-3" />
                    Reject
                  </Button>
                </>
              ) : (
                <p className="text-[11px] text-muted-foreground italic">
                  Analysis only — no rewrite to apply.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Confirmation popup ────────────────────────────────────────────────────
function ConfirmDialog({ open, onClose, onConfirm, estimate, options, models }) {
  if (!estimate) return null;
  const analysisModel = models.find((m) => m.id === options.analysis_model_id);
  const rewriteModel  = models.find((m) => m.id === options.rewrite_model_id);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="flex flex-col gap-0 p-0 overflow-hidden"
        style={{ maxWidth: '520px' }}
        data-testid="bulk-eval-confirm-dialog"
      >
        <DialogHeader className="border-b border-border px-5 py-3 bg-panel-header/40">
          <DialogTitle className="flex items-center gap-2 text-[14px]">
            <DollarSign className="h-4 w-4 text-primary" />
            Confirm Bulk Evaluation Job
          </DialogTitle>
        </DialogHeader>
        <div className="px-5 py-4 space-y-4">
          {/* Job summary */}
          <div className="rounded-md border border-border bg-muted/20 p-3 space-y-2 text-[12px]">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Novels selected</span>
              <span className="font-semibold">{estimate.project_count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Dimensions</span>
              <span className="font-semibold">{estimate.dimension_count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Mode</span>
              <span className="font-semibold capitalize">{options.mode === 'rewrite' ? 'Automated Rewrite' : 'Analysis Markup'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Plot context</span>
              <span className="font-semibold">{options.include_plot_context ? 'Included' : 'Excluded'}</span>
            </div>
          </div>

          {/* LLM summary */}
          <div className="rounded-md border border-border bg-muted/20 p-3 space-y-2 text-[12px]">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Analysis LLM</span>
              <span className="font-semibold">{analysisModel?.label || options.analysis_model_id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Rewrite LLM</span>
              <span className="font-semibold">{rewriteModel?.label || options.rewrite_model_id}</span>
            </div>
          </div>

          {/* Cost estimate */}
          <div className={cn(
            'rounded-md border p-3 space-y-2',
            estimate.estimate?.total_usd > 5 ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50',
          )}>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Estimated Cost
            </p>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-black tabular-nums text-foreground">
                ${estimate.estimate?.total_usd?.toFixed(3) ?? '—'}
              </span>
              <span className="text-[11px] text-muted-foreground">USD (approximate)</span>
            </div>
            <div className="grid grid-cols-2 gap-1 text-[10.5px] text-muted-foreground">
              <div>Analysis: <strong>${estimate.estimate?.analysis_usd?.toFixed(3)}</strong></div>
              <div>Rewrite: <strong>${estimate.estimate?.rewrite_usd?.toFixed(3)}</strong></div>
              <div>Input tokens: <strong>{(estimate.estimate?.total_input_tokens || 0).toLocaleString()}</strong></div>
              <div>Output tokens: <strong>{((estimate.estimate?.analysis_output_tokens || 0) + (estimate.estimate?.rewrite_output_tokens || 0)).toLocaleString()}</strong></div>
            </div>
            {estimate.estimate?.total_usd > 5 && (
              <div className="flex items-center gap-1.5 text-[11px] text-amber-800">
                <AlertTriangle className="h-3 w-3 shrink-0" />
                This is a high-cost job. Review your selections before proceeding.
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5 rounded-sm border border-sky-200/60 bg-sky-50/70 px-2.5 py-1.5 text-[11px] text-sky-800">
            <Info className="h-3 w-3 shrink-0" />
            Cost is estimated from word counts × model rates. Actual cost may differ ±20%.
          </div>
        </div>
        <DialogFooter className="border-t border-border bg-panel-header/40 px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="bulk-eval-confirm-cancel">
            Adjust Settings
          </Button>
          <Button size="sm" className="gap-1.5" onClick={onConfirm} data-testid="bulk-eval-confirm-run">
            <Play className="h-3.5 w-3.5" />
            Start Job
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Progress bar ──────────────────────────────────────────────────────────
function JobProgress({ job }) {
  if (!job) return null;
  const totalSteps = Math.max(1, job.total_dim_steps + job.total_rewrite_steps);
  const doneSteps  = (job.completed_dim_steps || 0) + (job.completed_rewrite_steps || 0);
  const pct        = Math.round((doneSteps / totalSteps) * 100);

  // ── ETA calculation ──
  let etaStr = '';
  if (job.status === 'running' && job.completed_projects > 0 && job.started_at) {
    const elapsedMs   = Date.now() - new Date(job.started_at).getTime();
    const msPerProject = elapsedMs / job.completed_projects;
    const remaining   = job.total_projects - job.completed_projects;
    const etaMs       = msPerProject * remaining;

    if (etaMs > 0) {
      const etaMins = Math.ceil(etaMs / 60000);
      etaStr = etaMins <= 1 ? '< 1 min remaining' : `~${etaMins} min remaining`;
    }
  }

  // ── Words-per-second throughput ──
  let wpsStr = '';
  if (job.total_words_processed > 0 && job.started_at) {
    const elapsedSec = (Date.now() - new Date(job.started_at).getTime()) / 1000;
    const wps = Math.round(job.total_words_processed / elapsedSec);
    if (wps > 0) wpsStr = `${wps.toLocaleString()} words/sec`;
  }

  const statusColor = {
    queued:    'text-sky-600',
    running:   'text-amber-600',
    done:      'text-emerald-600',
    cancelled: 'text-zinc-500',
    error:     'text-rose-600',
  }[job.status] || 'text-muted-foreground';

  return (
    <div className="rounded-md border border-border bg-card p-3 space-y-2" data-testid="bulk-eval-progress">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {job.status === 'running' ? (
            <Loader2 className="h-4 w-4 animate-spin text-amber-500" />
          ) : job.status === 'done' ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          ) : job.status === 'cancelled' ? (
            <XCircle className="h-4 w-4 text-zinc-400" />
          ) : (
            <Clock className="h-4 w-4 text-sky-500" />
          )}
          <span className={cn('text-[12.5px] font-semibold capitalize', statusColor)}>
            {job.status === 'running' ? 'Processing novels…' :
             job.status === 'done' ? 'Complete' :
             job.status === 'queued' ? 'Queued — starting soon' :
             job.status === 'cancelled' ? 'Cancelled' : job.status}
          </span>
        </div>
        <span className="text-[11.5px] text-muted-foreground tabular-nums">
          {job.completed_projects}/{job.total_projects} novels
        </span>
      </div>

      {/* Progress bar */}
      <div className="relative h-2 rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            'absolute inset-y-0 left-0 rounded-full transition-all duration-500',
            job.status === 'done' ? 'bg-emerald-500' :
            job.status === 'cancelled' ? 'bg-zinc-400' : 'bg-primary',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* ETA + throughput row */}
      {(etaStr || wpsStr) && (
        <div className="flex items-center justify-between text-[10.5px] text-muted-foreground">
          {etaStr && (
            <span className="flex items-center gap-1 text-amber-600 font-medium">
              <Clock className="h-3 w-3" />
              {etaStr}
            </span>
          )}
          {wpsStr && <span>{wpsStr}</span>}
        </div>
      )}

      {/* Per-project status dots */}
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(job.project_progress || {}).map(([pid, status]) => (
          <div
            key={pid}
            title={`${pid}: ${status}`}
            className={cn(
              'h-2.5 w-2.5 rounded-full',
              status === 'done'    ? 'bg-emerald-500' :
              status === 'running' ? 'bg-amber-500 animate-pulse' :
              status === 'error'   ? 'bg-rose-500' :
              'bg-muted-foreground/30',
            )}
          />
        ))}
      </div>

      {(job.status === 'running' || job.status === 'queued') && (
        <p className="text-[10.5px] text-muted-foreground">
          Analysis steps: {job.completed_dim_steps}/{job.total_dim_steps}
          {job.total_rewrite_steps > 0 && (
            <> · Rewrite steps: {job.completed_rewrite_steps}/{job.total_rewrite_steps}</>
          )}
        </p>
      )}
    </div>
  );
}

// ── Batch history row ─────────────────────────────────────────────────────
function HistoryRow({ job, onLoad }) {
  const cost = job.cost_estimate?.total_usd;
  return (
    <tr className="border-t border-border hover:bg-muted/15" data-testid={`bulk-eval-history-${job.job_id}`}>
      <td className="px-3 py-2 font-mono-stat text-[10.5px] text-muted-foreground max-w-[120px] truncate">
        {job.job_id}
      </td>
      <td className="px-3 py-2 text-[11.5px]">
        {new Date(job.created_at).toLocaleDateString()} {new Date(job.created_at).toLocaleTimeString()}
      </td>
      <td className="px-3 py-2 text-[11.5px] text-center">{job.total_projects}</td>
      <td className="px-3 py-2 text-[11.5px] text-center capitalize">{job.mode}</td>
      <td className="px-3 py-2 text-[11.5px] text-center">
        {cost != null ? `$${cost.toFixed(3)}` : '—'}
      </td>
      <td className="px-3 py-2 text-center">
        <Badge
          className={cn(
            'border text-[10px]',
            job.status === 'done'      ? 'border-emerald-300 bg-emerald-50 text-emerald-800' :
            job.status === 'running'   ? 'border-amber-300 bg-amber-50 text-amber-800' :
            job.status === 'cancelled' ? 'border-zinc-200 bg-zinc-100 text-zinc-600' :
            job.status === 'queued'    ? 'border-sky-200 bg-sky-50 text-sky-700' :
                                         'border-border bg-muted text-muted-foreground',
          )}
        >
          {job.status}
        </Badge>
      </td>
      <td className="px-3 py-2 text-center">
        <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => onLoad(job)}>
          View
        </Button>
      </td>
    </tr>
  );
}

// ── Bulk Manuscript Import Tab ────────────────────────────────────────────────
function BulkManuscriptImportTab() {
  const [users,         setUsers]         = useState([]);
  const [userSearch,    setUserSearch]     = useState('');
  const [targetUserId,  setTargetUserId]   = useState('');
  const [importFile,    setImportFile]     = useState(null);
  const [importing,     setImporting]      = useState(false);
  const [importResult,  setImportResult]   = useState(null);
  const [dragOver,      setDragOver]       = useState(false);
  const [reviewProjectId, setReviewProjectId] = useState(null);
  const fileInputRef = useRef(null);

  // Load users for picker
  useEffect(() => {
    adminApi.bulkManuscriptImportUsers(userSearch).then((d) => setUsers(d.users || [])).catch(() => {});
  }, [userSearch]);

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) { setImportFile(f); setImportResult(null); }
  };

  const runImport = async () => {
    if (!importFile || !targetUserId) return;
    setImporting(true);
    const tid = toast.loading('Parsing and importing manuscripts…');
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      fd.append('target_user_id', targetUserId);
      const result = await adminApi.bulkManuscriptImport(fd);
      setImportResult(result);
      if (result.projects_created > 0) {
        toast.success(`Imported ${result.projects_created} project${result.projects_created !== 1 ? 's' : ''}`, { id: tid });
        if (result.created?.[0]?.project_id) {
          setReviewProjectId(result.created[0].project_id);
        }
      } else {
        toast.warning('No projects were created — check the file format.', { id: tid });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed.', { id: tid });
    } finally {
      setImporting(false);
    }
  };

  const selectedUser = users.find((u) => u.user_id === targetUserId);
  const ACCEPT = '.zip,.docx,.txt,.pdf';

  return (
    <div className="space-y-5 max-w-xl">
      <div>
        <p className="text-[12px] text-muted-foreground leading-relaxed">
          Upload a ZIP archive (or a single .docx, .txt, or .pdf). Each file becomes a new project
          with chapters auto-detected from headings like "Chapter 1". Assign projects to any user.
        </p>
      </div>

      {/* User selector */}
      <div className="space-y-1.5">
        <label className="text-[12px] font-medium">Assign projects to</label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search user by name or email…"
              value={userSearch}
              onChange={(e) => { setUserSearch(e.target.value); setTargetUserId(''); }}
              className="h-8 w-full rounded-md border border-border bg-background pl-8 pr-3 text-[12px] outline-none focus:border-primary"
              data-testid="bulk-import-user-search"
            />
          </div>
        </div>
        {userSearch && users.length > 0 && !targetUserId && (
          <div className="rounded-md border border-border bg-background shadow-md">
            {users.slice(0, 8).map((u) => (
              <button
                key={u.user_id}
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-[12px] hover:bg-muted/60 text-left"
                onClick={() => { setTargetUserId(u.user_id); setUserSearch(u.name || u.email || u.user_id); }}
                data-testid={`bulk-import-user-opt-${u.user_id}`}
              >
                <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="truncate">{u.name || u.email || u.user_id}</span>
                <span className="ml-auto shrink-0 text-muted-foreground/60 font-mono text-[10px]">{u.email}</span>
              </button>
            ))}
          </div>
        )}
        {selectedUser && (
          <p className="text-[11px] text-emerald-700">
            Projects will be created for: <strong>{selectedUser.name || selectedUser.email}</strong>
          </p>
        )}
      </div>

      {/* Drop zone */}
      <div
        className={`relative flex min-h-[90px] cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-5 text-center transition-colors
          ${dragOver ? 'border-primary bg-primary/8' : 'border-border hover:border-primary/50 hover:bg-muted/40'}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        data-testid="bulk-import-admin-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          className="sr-only"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) { setImportFile(f); setImportResult(null); } }}
          data-testid="bulk-import-admin-file-input"
        />
        {importFile ? (
          <>
            <FileArchive className="h-6 w-6 text-primary" />
            <span className="text-[12px] font-medium truncate max-w-[280px]">{importFile.name}</span>
            <span className="text-[10.5px] text-muted-foreground">{(importFile.size / 1024).toFixed(1)} KB · Click to change</span>
          </>
        ) : (
          <>
            <Upload className="h-6 w-6 text-muted-foreground" />
            <span className="text-[12px] text-muted-foreground">Drop a file here or click to browse</span>
            <span className="text-[10.5px] text-muted-foreground/60">.zip · .docx · .txt · .pdf</span>
          </>
        )}
      </div>

      <Button
        className="w-full gap-2"
        onClick={runImport}
        disabled={!importFile || !targetUserId || importing}
        data-testid="bulk-import-admin-run-btn"
      >
        {importing
          ? <><Loader2 className="h-4 w-4 animate-spin" />Importing…</>
          : <><Upload className="h-4 w-4" />Import manuscripts</>}
      </Button>

      {/* Result summary */}
      {importResult && (
        <div className="rounded-md border border-border bg-muted/30 p-3 space-y-2 text-[12px]">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
            <span className="font-semibold">{importResult.projects_created} project{importResult.projects_created !== 1 ? 's' : ''} created</span>
            <span className="text-muted-foreground">for {importResult.target_user_email || targetUserId}</span>
          </div>
          {importResult.created?.map((c) => (
            <div key={c.project_id} className="flex items-center justify-between gap-2 border-b border-border/40 py-1 last:border-b-0 pl-6">
              <span className="truncate font-medium">{c.title}</span>
              <span className="shrink-0 text-muted-foreground">{c.chapter_count} ch · {(c.total_words || 0).toLocaleString()} words</span>
            </div>
          ))}
          {importResult.errors?.length > 0 && (
            <div className="mt-2 space-y-1">
              {importResult.errors.map((e, i) => (
                <div key={i} className="flex items-start gap-1.5 text-amber-700">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span className="break-all">{e.file}: {e.error}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* AI Extraction Review */}
      {reviewProjectId && (
        <div className="space-y-2 rounded-md border border-primary/30 bg-primary/5 p-3">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-[12.5px] font-semibold">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              AI Metadata Extraction — Review &amp; Apply
            </span>
            <button type="button" onClick={() => setReviewProjectId(null)} className="text-[10.5px] text-muted-foreground hover:text-foreground underline">Dismiss</button>
          </div>
          <ExtractionReviewPanel
            projectId={reviewProjectId}
            onApplied={() => toast.success('Metadata applied to project')}
          />
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function BulkNovelEvalPanel() {
  // Data
  const [allProjects, setAllProjects] = useState([]);
  const [models, setModels] = useState([]);
  const [cachingConfig, setCachingConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Selection
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  // Options
  const [selectedDims, setSelectedDims] = useState(DIMENSIONS.map((d) => d.key));
  const [mode, setMode] = useState('markup');
  const [analysisModel, setAnalysisModel] = useState('claude-sonnet-4-5');
  const [rewriteModel, setRewriteModel]   = useState('claude-sonnet-4-5');
  const [includePlot, setIncludePlot]     = useState(true);
  // Per-dimension model overrides: { dim_key: model_id }
  const [dimModelOverrides, setDimModelOverrides] = useState({});
  const [showDimOverrides, setShowDimOverrides]   = useState(false);

  // Dimension Override Templates — save/load named presets to localStorage
  const PRESETS_LS_KEY = 'bulk_eval_dim_presets';
  const [savedPresets, setSavedPresets] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bulk_eval_dim_presets') || '[]'); }
    catch { return []; }
  });
  const [presetName, setPresetName]     = useState('');
  const [loadPresetVal, setLoadPresetVal] = useState('');

  // Heat & Tension
  const [adjustTension,  setAdjustTension]   = useState(false);
  const [tensionDelta,   setTensionDelta]     = useState(0);
  const [adjustHeat,     setAdjustHeat]       = useState(false);
  const [heatDelta,      setHeatDelta]        = useState(0);
  const [htModel,        setHtModel]          = useState('claude-opus-4-5');

  // Confirmation popup
  const [showConfirm, setShowConfirm] = useState(false);
  const [estimate, setEstimate]       = useState(null);
  const [estimating, setEstimating]   = useState(false);

  // Active job
  const [activeJob, setActiveJob]     = useState(null);
  const [jobResults, setJobResults]   = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const pollRef = useRef(null);

  // History
  const [history, setHistory]         = useState([]);

  // Accept/reject/requeue state
  const [accepting, setAccepting]     = useState(null);
  const [rejecting, setRejecting]     = useState(null);
  const [requeuing, setRequeuing]     = useState(null);
  const [acceptingAll, setAcceptingAll] = useState(false);

  // Active view tab
  const [viewTab, setViewTab]         = useState('setup');

  // ── Load initial data ──────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [projData, histData] = await Promise.all([
        bulkNovelEvalApi.projects(),
        bulkNovelEvalApi.jobs(20),
      ]);
      setAllProjects(projData.projects || []);
      setHistory(histData.jobs || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load AI models + caching config
  useEffect(() => {
    loadData();

    // Fetch model list from the edit-walk endpoint (same catalog)
    const API = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    fetch(`${API}/api/edit-walk/models`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data?.models) {
          setModels(data.models);
          // Default to sonnet-4-5 if available
          const sonnet = data.models.find((m) => m.id === 'claude-sonnet-4-5');
          if (sonnet) {
            setAnalysisModel(sonnet.id);
            setRewriteModel(sonnet.id);
          }
        }
      })
      .catch(() => {});

    // Fetch caching config
    fetch(`${API}/api/admin/ai-caching`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data?.task_configs) {
          const cfg = {};
          for (const [key, val] of Object.entries(data.task_configs)) {
            cfg[key] = val.enabled ?? false;
          }
          setCachingConfig(cfg);
        }
      })
      .catch(() => {});
  }, [loadData]);

  // ── Polling ────────────────────────────────────────────────────────────
  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPoll = useCallback((jobId) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const job = await bulkNovelEvalApi.job(jobId);
        setActiveJob(job);
        if (['done', 'cancelled'].includes(job.status)) {
          stopPoll();
          // Load results
          setLoadingResults(true);
          const r = await bulkNovelEvalApi.results(jobId);
          setJobResults(r.results || []);
          setLoadingResults(false);
          if (job.status === 'done') {
            setViewTab('results');
            toast.success('Bulk evaluation complete.');
          }
          // Refresh history
          const h = await bulkNovelEvalApi.jobs(20);
          setHistory(h.jobs || []);
        }
      } catch { /* transient */ }
    }, POLL_MS);
  }, []);

  useEffect(() => () => stopPoll(), []);

  // ── Handlers ──────────────────────────────────────────────────────────
  const toggleProject = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length < 20 ? [...prev, id] : prev,
    );
  };

  const toggleDim = (key) => {
    setSelectedDims((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleEstimateAndConfirm = async () => {
    if (selectedIds.length === 0) { toast.error('Select at least one novel.'); return; }
    if (selectedDims.length === 0) { toast.error('Select at least one dimension.'); return; }
    setEstimating(true);
    try {
      const data = await bulkNovelEvalApi.estimate({
        project_ids:        selectedIds,
        dimensions:         selectedDims,
        mode,
        analysis_model_id:  analysisModel,
        rewrite_model_id:   rewriteModel,
        dim_model_overrides: dimModelOverrides,
      });
      setEstimate(data);
      setShowConfirm(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to estimate cost.');
    } finally {
      setEstimating(false);
    }
  };

  const handleStart = async () => {
    setShowConfirm(false);
    try {
      const res = await bulkNovelEvalApi.start({
        project_ids:          selectedIds,
        dimensions:           selectedDims,
        mode,
        analysis_model_id:    analysisModel,
        rewrite_model_id:     rewriteModel,
        include_plot_context: includePlot,
        dim_model_overrides:  dimModelOverrides,
        adjust_tension:       adjustTension,
        tension_delta:        tensionDelta,
        adjust_heat:          adjustHeat,
        heat_delta:           heatDelta,
        heat_tension_model_id: htModel,
      });
      toast.success(`Job started — ID: ${res.job_id}`);
      setActiveJob({ job_id: res.job_id, status: 'queued', cost_estimate: res.cost_estimate,
        total_projects: selectedIds.length, completed_projects: 0,
        total_dim_steps: selectedIds.length * selectedDims.length, completed_dim_steps: 0,
        total_rewrite_steps: 0, completed_rewrite_steps: 0,
        project_progress: Object.fromEntries(selectedIds.map((id) => [id, 'queued'])),
      });
      setJobResults([]);
      setViewTab('progress');
      startPoll(res.job_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start job.');
    }
  };

  // ── PDF Export ────────────────────────────────────────────────────────────
  const handleExportPdf = () => {
    if (!jobResults.length) return;
    const jobTitle = activeJob?.job_id ? `Bulk Eval — ${activeJob.job_id.slice(0, 8).toUpperCase()}` : 'Bulk Novel Evaluation';
    const date = new Date().toLocaleString();

    const scoreBar = (score) => {
      if (score == null) return '<span style="color:#6b7280">—</span>';
      const c = score >= 80 ? '#16a34a' : score >= 60 ? '#d97706' : '#dc2626';
      return `<span style="font-weight:700;color:${c}">${score}%</span>`;
    };

    const rows = jobResults.map((r) => {
      const dims = Object.entries(r.dim_findings || {}).map(([k, f]) => `
        <tr style="background:#f9fafb">
          <td style="padding:6px 10px;color:#6b7280;font-size:11px;border-top:1px solid #e5e7eb">&nbsp;&nbsp;&nbsp;${DIMENSIONS.find((d) => d.key === k)?.label || k}</td>
          <td style="padding:6px 10px;border-top:1px solid #e5e7eb;text-align:center">${scoreBar(f?.overall_score)}</td>
          <td style="padding:6px 10px;border-top:1px solid #e5e7eb;font-size:11px;color:#374151">${(f?.findings || []).slice(0, 2).join('<br/>') || '—'}</td>
          <td style="padding:6px 10px;border-top:1px solid #e5e7eb"></td>
        </tr>`).join('');

      const statusBadge = r.status === 'accepted'
        ? '<span style="background:#dcfce7;color:#166534;border:1px solid #bbf7d0;padding:2px 7px;border-radius:4px;font-size:10px;font-weight:600">Accepted</span>'
        : r.status === 'rejected'
        ? '<span style="background:#f4f4f5;color:#3f3f46;border:1px solid #d4d4d8;padding:2px 7px;border-radius:4px;font-size:10px;font-weight:600">Rejected</span>'
        : '<span style="background:#eff6ff;color:#1e40af;border:1px solid #bfdbfe;padding:2px 7px;border-radius:4px;font-size:10px;font-weight:600">Pending</span>';

      return `
        <tr style="background:#fff">
          <td style="padding:10px;font-weight:600;font-size:13px;border-top:2px solid #e5e7eb">${r.project_title || r.project_id}</td>
          <td style="padding:10px;border-top:2px solid #e5e7eb;text-align:center">${scoreBar(r.overall_score)}</td>
          <td style="padding:10px;border-top:2px solid #e5e7eb;font-size:11px;color:#374151">${(r.overall_commentary || '').slice(0, 200)}${r.overall_commentary?.length > 200 ? '…' : ''}</td>
          <td style="padding:10px;border-top:2px solid #e5e7eb;text-align:center">${statusBadge}</td>
        </tr>
        ${dims}`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${jobTitle}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 32px; color: #111827; }
  h1 { font-size: 22px; font-weight: 800; margin-bottom: 4px; }
  .meta { font-size: 12px; color: #6b7280; margin-bottom: 24px; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #111827; color: #fff; padding: 8px 10px; text-align: left; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; }
  th:nth-child(2) { text-align: center; width: 80px; }
  th:nth-child(4) { text-align: center; width: 90px; }
  @media print { body { padding: 0; } }
</style></head><body>
<h1>${jobTitle}</h1>
<p class="meta">Generated: ${date} · ${jobResults.length} novels · ${jobResults.filter((r) => r.status === 'accepted').length} accepted · ${jobResults.filter((r) => r.status === 'rejected').length} rejected</p>
<table>
  <thead><tr><th>Novel</th><th>Score</th><th>AI Commentary</th><th>Status</th></tr></thead>
  <tbody>${rows}</tbody>
</table></body></html>`;

    const win = window.open('', '_blank', 'width=900,height=700');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  const handleLoadJobFromHistory = async (job) => {
    setActiveJob(job);
    setViewTab('results');
    setLoadingResults(true);
    try {
      const r = await bulkNovelEvalApi.results(job.job_id);
      setJobResults(r.results || []);
    } catch (err) {
      toast.error('Failed to load results.');
    } finally {
      setLoadingResults(false);
    }
    if (['queued', 'running'].includes(job.status)) {
      setViewTab('progress');
      startPoll(job.job_id);
    }
  };

  const handleCancelJob = async () => {
    if (!activeJob) return;
    try {
      await bulkNovelEvalApi.cancel(activeJob.job_id);
      toast.success('Job cancelled.');
      stopPoll();
      setActiveJob((j) => ({ ...j, status: 'cancelled' }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to cancel.');
    }
  };

  const handleAcceptAllMarkup = async () => {
    if (!activeJob) return;
    setAcceptingAll(true);
    try {
      const res = await bulkNovelEvalApi.acceptAllMarkup(activeJob.job_id);
      toast.success(res.message || `Accepted ${res.accepted} results.`);
      // Refresh local state
      setJobResults((prev) =>
        prev.map((r) => {
          if (r.status === 'pending' && !r.rewrite_chapters?.length) {
            return { ...r, status: 'accepted' };
          }
          return r;
        }),
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Accept-all failed.');
    } finally {
      setAcceptingAll(false);
    }
  };

  const handleAccept = async (rid) => {
    setAccepting(rid);
    try {
      const res = await bulkNovelEvalApi.accept(rid);
      toast.success(`Accepted — ${res.chapters_applied ?? 0} chapters applied.`);
      setJobResults((prev) => prev.map((r) => r.result_id === rid ? { ...r, status: 'accepted' } : r));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Accept failed.');
    } finally {
      setAccepting(null);
    }
  };

  const handleReject = async (rid) => {
    setRejecting(rid);
    try {
      await bulkNovelEvalApi.reject(rid);
      toast.success('Result rejected.');
      setJobResults((prev) => prev.map((r) => r.result_id === rid ? { ...r, status: 'rejected' } : r));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reject failed.');
    } finally {
      setRejecting(null);
    }
  };

  const handleRequeue = async (rid, projectId) => {
    if (!activeJob) return;
    setRequeuing(rid);
    try {
      await bulkNovelEvalApi.requeue(activeJob.job_id, projectId);
      toast.success('Re-evaluation queued. Results will update shortly.');
      setJobResults((prev) => prev.map((r) =>
        r.result_id === rid ? { ...r, status: 'needs_reeval' } : r,
      ));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Re-queue failed.');
    } finally {
      setRequeuing(null);
    }
  };

  // ── Filtered projects ─────────────────────────────────────────────────
  const filtered = allProjects.filter((p) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (p.title || '').toLowerCase().includes(q) || (p.genre || '').toLowerCase().includes(q);
  });

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <PanelHeader
        title="Bulk Novel Evaluation & Rewrite"
        subtitle="Batch-analyse up to 20 novels across plot, character, structure, pacing, and continuity dimensions. Optionally auto-rewrite chapters and review changes before applying."
        onRefresh={loadData}
      />

      <Tabs value={viewTab} onValueChange={setViewTab}>
        <TabsList>
          <TabsTrigger value="setup" data-testid="bulk-eval-tab-setup">
            <Settings2 className="h-3.5 w-3.5 mr-1.5" />
            Setup
          </TabsTrigger>
          <TabsTrigger value="progress" data-testid="bulk-eval-tab-progress" disabled={!activeJob}>
            <Zap className="h-3.5 w-3.5 mr-1.5" />
            Progress
            {activeJob && ['queued', 'running'].includes(activeJob.status) && (
              <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
            )}
          </TabsTrigger>
          <TabsTrigger value="results" data-testid="bulk-eval-tab-results" disabled={jobResults.length === 0 && !loadingResults}>
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            Results {jobResults.length > 0 && `(${jobResults.length})`}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="bulk-eval-tab-history">
            <Clock className="h-3.5 w-3.5 mr-1.5" />
            History {history.length > 0 && `(${history.length})`}
          </TabsTrigger>
          <TabsTrigger value="import" data-testid="bulk-eval-tab-import">
            <Upload className="h-3.5 w-3.5 mr-1.5" />
            Import
          </TabsTrigger>
        </TabsList>

        {/* ── SETUP TAB ─────────────────────────────────────────────────── */}
        <TabsContent value="setup" className="mt-4 space-y-5">
          {loading ? (
            <div className="flex items-center gap-2 py-10 justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading projects…
            </div>
          ) : (
            <>
              {/* ── 1. Project selection ───────────────────────────────── */}
              <div className="rounded-md border border-border bg-card p-4 space-y-3 shadow-panel">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-[13px] font-semibold flex items-center gap-1.5">
                      <BookOpen className="h-4 w-4" />
                      Select Novels
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Choose up to 20 novels to evaluate.{' '}
                      <span className={cn('font-semibold', selectedIds.length >= 20 ? 'text-amber-600' : 'text-foreground')}>
                        {selectedIds.length}/20 selected
                      </span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedIds.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-[11px]"
                        onClick={() => setSelectedIds([])}
                        data-testid="bulk-eval-clear-selection"
                      >
                        <Square className="h-3 w-3 mr-1" />
                        Clear
                      </Button>
                    )}
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input
                        className="h-8 pl-8 w-52 text-[12px]"
                        placeholder="Search title or genre…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="bulk-eval-search"
                      />
                    </div>
                  </div>
                </div>

                <ScrollArea className="h-[280px] pr-2">
                  <div className="grid grid-cols-2 gap-2">
                    {filtered.length === 0 && (
                      <div className="col-span-2 py-6 text-center text-[12px] text-muted-foreground">
                        No projects found.
                      </div>
                    )}
                    {filtered.map((p, i) => (
                      <ProjectCard
                        key={`${p.id}_${i}`}
                        project={p}
                        selected={selectedIds.includes(p.id)}
                        onToggle={toggleProject}
                        disabled={!selectedIds.includes(p.id) && selectedIds.length >= 20}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </div>

              {/* ── 2. Dimensions ─────────────────────────────────────── */}
              <div className="rounded-md border border-border bg-card p-4 space-y-3 shadow-panel">
                <p className="text-[13px] font-semibold flex items-center gap-1.5">
                  <Layers className="h-4 w-4" />
                  Analysis Dimensions
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {DIMENSIONS.map((d) => (
                    <label
                      key={d.key}
                      className={cn(
                        'flex items-start gap-2.5 rounded-md border px-3 py-2 cursor-pointer transition-colors',
                        selectedDims.includes(d.key)
                          ? 'border-primary/40 bg-primary/5'
                          : 'border-border hover:bg-muted/20',
                      )}
                      data-testid={`bulk-eval-dim-${d.key}`}
                    >
                      <Checkbox
                        checked={selectedDims.includes(d.key)}
                        onCheckedChange={() => toggleDim(d.key)}
                        className="mt-0.5"
                      />
                      <div>
                        <p className="text-[12px] font-semibold">{d.label}</p>
                        <p className="text-[10.5px] text-muted-foreground leading-snug">{d.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* ── 3. Mode + LLMs + Options ───────────────────────────── */}
              <div className="rounded-md border border-border bg-card p-4 space-y-4 shadow-panel">
                <p className="text-[13px] font-semibold flex items-center gap-1.5">
                  <Settings2 className="h-4 w-4" />
                  Processing Options
                </p>

                {/* Mode toggle */}
                <div className="space-y-1.5">
                  <Label className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Output Mode
                  </Label>
                  <div className="flex rounded-md border border-border overflow-hidden w-fit">
                    <button
                      type="button"
                      data-testid="bulk-eval-mode-markup"
                      onClick={() => setMode('markup')}
                      className={cn(
                        'px-4 py-1.5 text-[12px] font-medium transition-colors',
                        mode === 'markup' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground',
                      )}
                    >
                      Analysis Markup
                    </button>
                    <button
                      type="button"
                      data-testid="bulk-eval-mode-rewrite"
                      onClick={() => setMode('rewrite')}
                      className={cn(
                        'px-4 py-1.5 text-[12px] font-medium transition-colors',
                        mode === 'rewrite' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground',
                      )}
                    >
                      Automated Rewrite
                    </button>
                  </div>
                  <p className="text-[10.5px] text-muted-foreground">
                    {mode === 'markup'
                      ? 'Returns findings and recommendations per dimension. No chapters are modified.'
                      : 'Generates rewritten chapter prose addressing all issues. Admin must accept/reject before changes apply.'}
                  </p>
                </div>

                <Separator />

                {/* LLM selectors */}
                <div className="grid grid-cols-2 gap-4">
                  <ModelSelect
                    label="Analysis LLM"
                    value={analysisModel}
                    onChange={setAnalysisModel}
                    models={models.length > 0 ? models : [{ id: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5', provider: 'anthropic', cost_tier: 'standard' }]}
                    cachingConfig={cachingConfig}
                    testId="bulk-eval-analysis-model"
                  />
                  <ModelSelect
                    label="Rewrite LLM"
                    value={rewriteModel}
                    onChange={setRewriteModel}
                    models={models.length > 0 ? models : [{ id: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5', provider: 'anthropic', cost_tier: 'standard' }]}
                    cachingConfig={cachingConfig}
                    testId="bulk-eval-rewrite-model"
                  />
                </div>

                <div className="flex items-start gap-2 text-[11.5px] text-muted-foreground">
                  <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-sky-500" />
                  Caching indicator: <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 mx-1" /> enabled ·{' '}
                  <span className="inline-block h-2 w-2 rounded-full bg-amber-400 mx-1" /> disabled ·{' '}
                  <span className="inline-block h-2 w-2 rounded-full bg-zinc-300 mx-1" /> not supported
                </div>

                {/* Per-dimension LLM override section */}
                <div className="rounded-md border border-border bg-card/50">
                  <button
                    type="button"
                    onClick={() => setShowDimOverrides((v) => !v)}
                    data-testid="bulk-eval-dim-overrides-toggle"
                    className="flex w-full items-center justify-between px-3 py-2 text-[12px] font-semibold text-foreground hover:bg-muted/10 transition-colors"
                  >
                    <span className="flex items-center gap-1.5">
                      <Settings2 className="h-3.5 w-3.5 text-muted-foreground" />
                      Per-dimension LLM overrides
                      {Object.keys(dimModelOverrides).length > 0 && (
                        <span className="ml-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary">
                          {Object.keys(dimModelOverrides).length} set
                        </span>
                      )}
                    </span>
                    {showDimOverrides ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
                  </button>

                  {showDimOverrides && (
                    <div className="border-t border-border px-3 py-3 space-y-2">
                      <p className="text-[10.5px] text-muted-foreground leading-snug">
                        Override the Analysis LLM for specific dimensions. Leave blank to use the batch-level Analysis LLM above.
                        Useful for routing cheaper dimensions to faster models while using Opus for complex ones.
                      </p>
                      <div className="grid grid-cols-1 gap-2.5">
                        {DIMENSIONS.filter((d) => selectedDims.includes(d.key)).map((dim) => (
                          <div key={dim.key} className="flex items-center gap-2" data-testid={`bulk-eval-dim-override-${dim.key}`}>
                            <span className="w-32 shrink-0 text-[11.5px] font-medium text-foreground">{dim.label}</span>
                            <select
                              value={dimModelOverrides[dim.key] || ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setDimModelOverrides((prev) => {
                                  if (!v) { const n = { ...prev }; delete n[dim.key]; return n; }
                                  return { ...prev, [dim.key]: v };
                                });
                              }}
                              className="h-7 flex-1 rounded border border-border bg-background px-2 text-[11.5px] focus:outline-none focus:ring-1 focus:ring-ring"
                            >
                              <option value="">— Use batch model ({models.find((m) => m.id === analysisModel)?.label || analysisModel}) —</option>
                              {models.map((m) => (
                                <option key={m.id} value={m.id}>{m.label} ({m.cost_tier || m.provider})</option>
                              ))}
                            </select>
                          </div>
                        ))}
                        {selectedDims.length === 0 && (
                          <p className="text-[11px] text-muted-foreground italic">Select dimensions above to configure per-dimension models.</p>
                        )}
                      </div>
                      {Object.keys(dimModelOverrides).length > 0 && (
                        <button
                          type="button"
                          className="text-[11px] text-rose-500 hover:text-rose-700 transition-colors"
                          onClick={() => setDimModelOverrides({})}
                        >
                          Clear all overrides
                        </button>
                      )}

                      {/* ── Dimension Override Templates (Presets) ── */}
                      <div className="pt-2 border-t border-border/40 space-y-2">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Override Presets
                        </p>
                        {/* Load preset */}
                        {savedPresets.length > 0 && (
                          <div className="flex items-center gap-2">
                            <select
                              value={loadPresetVal}
                              onChange={(e) => {
                                const name = e.target.value;
                                setLoadPresetVal(name);
                                if (!name) return;
                                const preset = savedPresets.find((p) => p.name === name);
                                if (preset) {
                                  setDimModelOverrides(preset.overrides);
                                  toast.success(`Preset "${name}" loaded`);
                                }
                              }}
                              className="h-7 flex-1 rounded border border-border bg-background px-2 text-[11.5px] focus:outline-none focus:ring-1 focus:ring-ring"
                              data-testid="bulk-eval-load-preset"
                            >
                              <option value="">— Load a preset —</option>
                              {savedPresets.map((p) => (
                                <option key={p.name} value={p.name}>{p.name}</option>
                              ))}
                            </select>
                            {loadPresetVal && (
                              <button
                                type="button"
                                title={`Delete preset "${loadPresetVal}"`}
                                className="text-[11px] text-rose-500 hover:text-rose-700 transition-colors px-1"
                                data-testid="bulk-eval-delete-preset-btn"
                                onClick={() => {
                                  const next = savedPresets.filter((p) => p.name !== loadPresetVal);
                                  setSavedPresets(next);
                                  localStorage.setItem(PRESETS_LS_KEY, JSON.stringify(next));
                                  setLoadPresetVal('');
                                  toast.success(`Preset deleted`);
                                }}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        )}
                        {/* Save current as named preset */}
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            placeholder="Name this preset…"
                            value={presetName}
                            onChange={(e) => setPresetName(e.target.value)}
                            className="h-7 flex-1 rounded border border-border bg-background px-2 text-[11.5px] focus:outline-none focus:ring-1 focus:ring-ring"
                            data-testid="bulk-eval-preset-name-input"
                          />
                          <button
                            type="button"
                            disabled={!presetName.trim() || Object.keys(dimModelOverrides).length === 0}
                            onClick={() => {
                              const name = presetName.trim();
                              const next = savedPresets.filter((p) => p.name !== name);
                              next.push({ name, overrides: { ...dimModelOverrides } });
                              setSavedPresets(next);
                              localStorage.setItem(PRESETS_LS_KEY, JSON.stringify(next));
                              setPresetName('');
                              setLoadPresetVal(name);
                              toast.success(`Preset "${name}" saved`);
                            }}
                            className="h-7 shrink-0 rounded border border-border bg-background px-2.5 text-[11px] font-medium hover:bg-muted/20 disabled:opacity-40 transition-colors"
                            data-testid="bulk-eval-save-preset-btn"
                          >
                            Save
                          </button>
                        </div>
                        {Object.keys(dimModelOverrides).length === 0 && (
                          <p className="text-[10.5px] text-muted-foreground italic">Set at least one override above to save a preset.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Plot context toggle */}
                <label className="flex items-start gap-3 cursor-pointer" data-testid="bulk-eval-plot-context-toggle">
                  <Checkbox
                    checked={includePlot}
                    onCheckedChange={(v) => setIncludePlot(!!v)}
                    className="mt-0.5"
                  />
                  <div>
                    <p className="text-[12.5px] font-semibold">Include Plot Extraction Context</p>
                    <p className="text-[10.5px] text-muted-foreground leading-snug">
                      Injects each novel's cached plot elements (exposition, inciting incident, act structure, resolution) into
                      the LLM system prompt. Gives the analysis model structural intent to compare against — strongly recommended.
                    </p>
                  </div>
                </label>

                <Separator />

                {/* Heat & Tension section */}
                <div className="space-y-3">
                  <p className="text-[12px] font-semibold flex items-center gap-1.5">
                    <Flame className="h-3.5 w-3.5 text-rose-500" />
                    Heat &amp; Tension Adjustment
                    <span className="ml-1 text-[10px] font-normal text-muted-foreground">(Opus 4.5+ only)</span>
                  </p>

                  <label className="flex items-start gap-3 cursor-pointer" data-testid="bulk-eval-adjust-tension-toggle">
                    <Checkbox
                      checked={adjustTension}
                      onCheckedChange={(v) => setAdjustTension(!!v)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <p className="text-[12.5px] font-semibold flex items-center gap-1.5">
                        <Wind className="h-3 w-3 text-sky-500" />
                        Adjust Emotional Tension
                      </p>
                      <p className="text-[10.5px] text-muted-foreground leading-snug">
                        Calibrates conflict, internal struggle, dread, and narrative stakes.
                      </p>
                    </div>
                    {adjustTension && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button type="button" className="h-7 w-7 rounded border border-border bg-muted text-sm font-bold" onClick={() => setTensionDelta((v) => Math.max(-100, v - 10))}>−</button>
                        <input
                          type="number" min={-100} max={100} step={5}
                          value={tensionDelta}
                          onChange={(e) => setTensionDelta(Math.max(-100, Math.min(100, Number(e.target.value))))}
                          className={cn('h-7 w-16 rounded border text-center text-[12px] font-bold bg-background focus:outline-none focus:ring-1 focus:ring-ring', tensionDelta > 0 ? 'border-emerald-300 text-emerald-700' : tensionDelta < 0 ? 'border-rose-300 text-rose-700' : 'border-border')}
                          data-testid="bulk-eval-tension-delta"
                        />
                        <span className="text-[11px] text-muted-foreground">%</span>
                        <button type="button" className="h-7 w-7 rounded border border-border bg-muted text-sm font-bold" onClick={() => setTensionDelta((v) => Math.min(100, v + 10))}>+</button>
                      </div>
                    )}
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer" data-testid="bulk-eval-adjust-heat-toggle">
                    <Checkbox
                      checked={adjustHeat}
                      onCheckedChange={(v) => setAdjustHeat(!!v)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <p className="text-[12.5px] font-semibold flex items-center gap-1.5">
                        <Flame className="h-3 w-3 text-rose-500" />
                        Adjust Sexual Heat
                      </p>
                      <p className="text-[10.5px] text-muted-foreground leading-snug">
                        Calibrates sensuality, romantic charge, and physical intimacy levels.
                      </p>
                    </div>
                    {adjustHeat && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button type="button" className="h-7 w-7 rounded border border-border bg-muted text-sm font-bold" onClick={() => setHeatDelta((v) => Math.max(-100, v - 10))}>−</button>
                        <input
                          type="number" min={-100} max={100} step={5}
                          value={heatDelta}
                          onChange={(e) => setHeatDelta(Math.max(-100, Math.min(100, Number(e.target.value))))}
                          className={cn('h-7 w-16 rounded border text-center text-[12px] font-bold bg-background focus:outline-none focus:ring-1 focus:ring-ring', heatDelta > 0 ? 'border-emerald-300 text-emerald-700' : heatDelta < 0 ? 'border-rose-300 text-rose-700' : 'border-border')}
                          data-testid="bulk-eval-heat-delta"
                        />
                        <span className="text-[11px] text-muted-foreground">%</span>
                        <button type="button" className="h-7 w-7 rounded border border-border bg-muted text-sm font-bold" onClick={() => setHeatDelta((v) => Math.min(100, v + 10))}>+</button>
                      </div>
                    )}
                  </label>

                  {(adjustTension || adjustHeat) && (
                    <div className="space-y-2 pl-7">
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                          <Zap className="h-3 w-3" /> Heat/Tension LLM (Opus only)
                        </Label>
                        <select
                          value={htModel}
                          onChange={(e) => setHtModel(e.target.value)}
                          data-testid="bulk-eval-ht-model"
                          className="h-8 w-full rounded-md border border-border bg-background px-2.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-ring"
                        >
                          <option value="claude-opus-4-5">Claude Opus 4.5 — Recommended</option>
                          <option value="claude-opus-4-7">Claude Opus 4.7 — Latest</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-1.5 text-[10.5px] text-amber-700 rounded border border-amber-200 bg-amber-50/60 px-2 py-1.5">
                        <Info className="h-3 w-3 shrink-0 text-amber-500" />
                        Heat/tension adjustments run as a separate rewrite pass per chapter using Opus.
                        This will increase job cost and duration significantly for long novels.
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Run button ───────────────────────────────────────── */}
              <div className="flex items-center justify-between">
                <p className="text-[11.5px] text-muted-foreground">
                  {selectedIds.length === 0
                    ? 'Select at least 1 novel to proceed.'
                    : `${selectedIds.length} novel${selectedIds.length > 1 ? 's' : ''} · ${selectedDims.length} dimension${selectedDims.length > 1 ? 's' : ''} · ${mode === 'rewrite' ? 'Automated Rewrite' : 'Markup'}`}
                </p>
                <Button
                  onClick={handleEstimateAndConfirm}
                  disabled={estimating || selectedIds.length === 0 || selectedDims.length === 0}
                  className="gap-2"
                  data-testid="bulk-eval-run-btn"
                >
                  {estimating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <DollarSign className="h-4 w-4" />
                  )}
                  {estimating ? 'Estimating…' : 'Review Cost & Run'}
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        {/* ── PROGRESS TAB ──────────────────────────────────────────────── */}
        <TabsContent value="progress" className="mt-4 space-y-3">
          {activeJob ? (
            <>
              <div className="flex items-center justify-between">
                <p className="text-[11.5px] text-muted-foreground font-mono-stat">
                  Job: {activeJob.job_id}
                </p>
                {['queued', 'running'].includes(activeJob.status) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11.5px] text-destructive border-destructive/30 hover:bg-destructive/10"
                    onClick={handleCancelJob}
                    data-testid="bulk-eval-cancel-btn"
                  >
                    <XCircle className="h-3 w-3 mr-1" />
                    Cancel Job
                  </Button>
                )}
              </div>
              <JobProgress job={activeJob} />

              {/* Cost estimate display */}
              {activeJob.cost_estimate && (
                <div className="rounded-md border border-border bg-muted/20 p-3 text-[11.5px] space-y-1">
                  <p className="font-semibold text-muted-foreground uppercase tracking-wider text-[10px]">
                    Estimated Cost
                  </p>
                  <p className="text-[16px] font-bold tabular-nums">
                    ${activeJob.cost_estimate.total_usd?.toFixed(3)}
                  </p>
                  <p className="text-[10.5px] text-muted-foreground">
                    Analysis: ${activeJob.cost_estimate.analysis_usd?.toFixed(3)} · 
                    Rewrite: ${activeJob.cost_estimate.rewrite_usd?.toFixed(3)}
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="py-12 text-center text-muted-foreground text-[12.5px]">
              No active job. Start a job from the Setup tab.
            </div>
          )}
        </TabsContent>

        {/* ── RESULTS TAB ───────────────────────────────────────────────── */}
        <TabsContent value="results" className="mt-4 space-y-3">
          {loadingResults ? (
            <div className="flex items-center gap-2 py-10 justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading results…
            </div>
          ) : jobResults.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground text-[12.5px]">
              No results yet — run a job or load one from History.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-[12px] text-muted-foreground">
                  {jobResults.length} novel{jobResults.length > 1 ? 's' : ''} evaluated ·{' '}
                  {jobResults.filter((r) => r.status === 'accepted').length} accepted ·{' '}
                  {jobResults.filter((r) => r.status === 'rejected').length} rejected
                </p>
                <div className="flex items-center gap-2">
                  {/* Accept All Markup: only show when there are markup-only pending results */}
                  {jobResults.some((r) => r.status === 'pending' && !r.rewrite_chapters?.length) && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11.5px] gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      disabled={acceptingAll}
                      onClick={handleAcceptAllMarkup}
                      data-testid="bulk-eval-accept-all-markup"
                    >
                      {acceptingAll ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-3 w-3" />
                      )}
                      {acceptingAll ? 'Accepting…' : 'Accept All Analysis Results'}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px] gap-1.5 border-primary/40 text-primary hover:bg-primary/5"
                    onClick={handleExportPdf}
                    data-testid="bulk-eval-export-pdf"
                    title="Export all results to PDF"
                  >
                    <FileDown className="h-3 w-3" />
                    Export PDF
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px] gap-1.5"
                    onClick={async () => {
                      if (!activeJob) return;
                      setLoadingResults(true);
                      try {
                        const r = await bulkNovelEvalApi.results(activeJob.job_id);
                        setJobResults(r.results || []);
                      } finally {
                        setLoadingResults(false);
                      }
                    }}
                  >
                    <RefreshCw className="h-3 w-3" />
                    Refresh
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                {jobResults.map((result) => (
                  <NovelResultCard
                    key={result.result_id}
                    result={result}
                    onAccept={handleAccept}
                    onReject={handleReject}
                    onRequeue={handleRequeue}
                    accepting={accepting === result.result_id}
                    rejecting={rejecting === result.result_id}
                    requeuing={requeuing === result.result_id}
                  />
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* ── HISTORY TAB ───────────────────────────────────────────────── */}
        <TabsContent value="history" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[12px] text-muted-foreground">{history.length} past batch jobs</p>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px] gap-1.5"
              onClick={loadData}
            >
              <RefreshCw className="h-3 w-3" />
              Refresh
            </Button>
          </div>
          {history.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground text-[12.5px] rounded-md border border-dashed border-border">
              No batch runs yet.
            </div>
          ) : (
            <div className="rounded-md border border-border overflow-hidden">
              <table className="w-full text-[12px]">
                <thead className="bg-muted/40 text-muted-foreground text-[10.5px] uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left">Job ID</th>
                    <th className="px-3 py-2 text-left">Started</th>
                    <th className="px-3 py-2 text-center">Novels</th>
                    <th className="px-3 py-2 text-center">Mode</th>
                    <th className="px-3 py-2 text-center">Est. Cost</th>
                    <th className="px-3 py-2 text-center">Status</th>
                    <th className="px-3 py-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((job) => (
                    <HistoryRow key={job.job_id} job={job} onLoad={handleLoadJobFromHistory} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ── IMPORT TAB ─────────────────────────────────────────────────── */}
        <TabsContent value="import" className="mt-4">
          <BulkManuscriptImportTab />
        </TabsContent>
      </Tabs>

      {/* Confirmation dialog */}
      <ConfirmDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleStart}
        estimate={estimate}
        options={{ mode, analysis_model_id: analysisModel, rewrite_model_id: rewriteModel, include_plot_context: includePlot }}
        models={models.length > 0 ? models : [
          { id: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5', provider: 'anthropic' },
        ]}
      />
    </div>
  );
}
